import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '@/components/SEO';
import { seoMeta } from '@/lib/meta';

const seoKeys: Record<string, string> = {
  '/': 'home',
  '/dashboard': 'dashboard',
  '/auth': 'auth',
  '/chat': 'chat',
  '/study': 'study',
  '/community': 'community',
  '/flashcards': 'flashcards',
  '/revision-sheets': 'revisionSheets',
  '/calculator': 'calculator',
  '/checklists': 'checklists',
  '/guidelines': 'guidelines',
  '/quiz': 'quiz',
  '/daily-quiz': 'dailyQuiz',
  '/scores': 'scores',
  '/medications': 'medications',
  '/coding': 'coding',
  '/rss-aggregator': 'rss',
  '/settings': 'settings',
  '/not-found': 'notFound',
  '/calculateurs': 'calculateurs',
  '/blog': 'blog',
  '/cgu': 'cgu',
  '/privacy': 'privacy',
  '/mentions-legales': 'mentionsLegales',
  '/legal': 'mentionsLegales',
  '/pricing': 'pricing',
  '/articles': 'articles',
  '/features': 'features',
  '/a-propos': 'aboutDoctor',
};

function resolveSEOKey(pathname: string): string {
  if (seoKeys[pathname]) return seoKeys[pathname];

  if (pathname.startsWith('/calculateur/')) return 'calculateur';
  if (pathname.startsWith('/blog/')) return 'blogPost';
  if (pathname.startsWith('/articles/')) return 'articlePost';

  if (pathname.startsWith('/admin')) {
    if (pathname === '/admin' || pathname === '/admin/') return 'adminDashboard';
    if (pathname.includes('usage-stats')) return 'adminUsage';
    if (pathname.includes('ai-prompts')) return 'adminPrompts';
    if (pathname.includes('notifications')) return 'adminNotifications';
    if (pathname.includes('users')) return 'adminUsers';
    if (pathname.includes('system-logs')) return 'adminLogs';
    if (pathname.includes('push-debug')) return 'adminPushDebug';
    if (pathname.includes('send-samples')) return 'adminSamples';
    return 'admin';
  }

  return 'fallback';
}

export const RouteSEO: React.FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const key = resolveSEOKey(pathname);
  const title = t(`common.seo.${key}.title`);
  const description = t(`common.seo.${key}.desc`);
  const noIndex = pathname.startsWith('/admin') || pathname.startsWith('/auth');

  const origin = seoMeta.productionUrl;
  const { founder } = seoMeta;

  const websiteJsonLd = {
    '@type': 'WebSite',
    name: seoMeta.ogSiteName,
    url: origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${origin}/chat?query={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const structuredData = pathname === '/'
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': ['Organization', 'MedicalOrganization'],
            name: 'Sclépios I.A',
            url: origin,
            logo: `${origin}/lovable-uploads/438907fc-0c04-485c-b965-aa515e4258e4.png`,
            description: 'Assistant IA pour professionnels de santé — chat médical, calculateurs de scores, flashcards et cas cliniques',
            founder: {
              '@type': 'Person',
              name: founder.name,
              jobTitle: founder.jobTitle,
              sameAs: founder.linkedIn,
            },
            sameAs: [
              'https://sclepios-ia.com',
            ],
          },
          {
            '@type': ['SoftwareApplication', 'MobileApplication'],
            name: 'Sclépios IA — Assistant médical IA',
            applicationCategory: 'MedicalApplication',
            operatingSystem: 'iOS, Android, Web',
            url: origin,
            description: 'Chat médical IA, 67+ calculateurs de scores cliniques, flashcards médicales, quiz et cas cliniques pour médecins, infirmiers et internes.',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'EUR',
              description: 'Essai gratuit 7 jours',
            },
            featureList: [
              'Chat médical IA spécialisé',
              '67+ calculateurs de scores cliniques',
              'Flashcards médicales',
              'Quiz et cas cliniques',
              'Analyse d\'ordonnances',
              'Codes CIM10',
              'Droit de la santé',
            ],
            author: {
              '@type': 'Person',
              name: founder.name,
              jobTitle: founder.jobTitle,
            },
          },
          websiteJsonLd,
        ],
      }
    : { '@context': 'https://schema.org', ...websiteJsonLd };

  return <SEO title={title} description={description} noIndex={noIndex} structuredData={structuredData} />;
};
